import HeadingItem from '@modules/utilities/interfaces/HeadingItem';
import { MemberType } from '@modules/client';
import {
  propertiesID,
  methodsID,
  eventsID,
  callbacksID,
  functionsID,
  constructorsID,
  mathOperationsID,
  summaryID,
  memberTypeBySectionHash,
  REGEX_INHERIT,
  summaryDashID,
} from '../../constants/apiReferenceConstants';

/**
 * Helper to check if a heading is a section heading
 */
const isSectionHeading = (headingSlug: string, headingLevel: number): boolean => {
  const slug = headingSlug.toLowerCase();
  return (
    slug === summaryID ||
    slug === propertiesID ||
    slug === methodsID ||
    slug === eventsID ||
    slug === callbacksID ||
    slug === functionsID ||
    slug === constructorsID ||
    slug === mathOperationsID ||
    slug.startsWith(summaryDashID) ||
    headingLevel <= 2
  );
};

const sectionHasItems = (
  headings: HeadingItem[],
  sectionIndex: number,
  sectionLevel: number,
  sectionSlug: string,
): boolean => {
  // Special case: summary sub-sections delegate to their corresponding main section
  if (sectionLevel === 3 && sectionSlug.startsWith(summaryDashID)) {
    const mainSectionSlug = sectionSlug.replace(summaryDashID, '');
    // Find the main section anywhere in the headings array
    for (let i = 0; i < headings.length; i += 1) {
      const heading = headings[i];
      const slug = heading.slug.toLowerCase();
      if (slug === mainSectionSlug && heading.level === 2) {
        // Recursively check if main section has non-inherited items
        return sectionHasItems(headings, i, 2, slug);
      }
    }
    // Main section not found
    return false;
  }

  // Special case: main "summary" section - check if any summary-* sub-section has items
  if (sectionSlug === summaryID) {
    for (let i = sectionIndex + 1; i < headings.length; i += 1) {
      const heading = headings[i];
      const slug = heading.slug.toLowerCase();
      // Stop at next level 2 section
      if (heading.level === 2 && slug !== summaryID) break;
      // Check if this summary sub-section has items
      if (slug.startsWith(summaryDashID) && heading.level === 3) {
        if (sectionHasItems(headings, i, 3, slug)) return true;
      }
    }
    return false;
  }

  // General case: scan for non-inherited items
  let insideInheritedSection = false;

  for (let i = sectionIndex + 1; i < headings.length; i += 1) {
    const heading = headings[i];
    const slug = heading.slug.toLowerCase();
    const isInherited = REGEX_INHERIT.test(slug);

    // Check if this is a main section heading (level 2 or specific section IDs)
    const isMainSection =
      heading.level === 2 ||
      (heading.level === 3 &&
        (slug === propertiesID ||
          slug === methodsID ||
          slug === eventsID ||
          slug === callbacksID ||
          slug === functionsID ||
          slug === constructorsID ||
          slug === mathOperationsID));

    // Entering inherited section
    if (isInherited) {
      insideInheritedSection = true;
    } else if (isMainSection) {
      // Hit a main section at same or higher level - end of current section
      if (heading.level <= sectionLevel) {
        return false;
      }
      // Level 3 section means we're exiting an inherited section
      insideInheritedSection = false;
    } else if (
      !insideInheritedSection &&
      heading.level > sectionLevel &&
      !isSectionHeading(slug, heading.level)
    ) {
      // Found a non-inherited, non-section item
      return true;
    }
  }

  return false;
};

/**
 * Filters engine reference headings to exclude inherited sections and empty sections
 */
export const filterEngineReferenceHeadings = (headings: HeadingItem[]): HeadingItem[] => {
  // Helper to check if a section heading has non-inherited items following it

  const filtered: HeadingItem[] = [];
  let skipUntilNextSection = false;

  for (let i = 0; i < headings.length; i += 1) {
    const heading = headings[i];
    const slug = heading.slug.toLowerCase();

    // Check if this is an "Inherited from" heading
    const isInherited = REGEX_INHERIT.test(slug);

    if (isInherited) {
      // Skip this heading and all subsequent headings until we hit a section heading
      skipUntilNextSection = true;
    } else if (skipUntilNextSection) {
      // If we're skipping, check if we've hit a section heading
      if (isSectionHeading(slug, heading.level)) {
        skipUntilNextSection = false;
        // Only include section heading if it has items
        if (sectionHasItems(headings, i, heading.level, slug)) {
          filtered.push(heading);
        }
      }
      // Otherwise continue skipping (don't add to filtered)
    } else {
      // Check if this is a section heading
      const isCurrentSectionHeading = isSectionHeading(slug, heading.level);
      if (isCurrentSectionHeading) {
        // Only include section heading if it has items
        if (sectionHasItems(headings, i, heading.level, slug)) {
          filtered.push(heading);
        }
        // If section heading has no items, don't include it
      } else {
        // Not a section heading, include it
        filtered.push(heading);
      }
    }
  }

  return filtered;
};

/**
 * Determines the member type from a heading based on the section it belongs to
 */
export const getMemberTypeFromHeading = (
  heading: HeadingItem,
  index: number,
  allHeadings: HeadingItem[],
): MemberType | null => {
  const slug = heading.slug.toLowerCase();

  // Check if it's a section heading
  if (
    slug === summaryID ||
    slug === propertiesID ||
    slug === methodsID ||
    slug === eventsID ||
    slug === callbacksID ||
    slug === functionsID ||
    slug === constructorsID ||
    slug === mathOperationsID ||
    slug.startsWith(summaryDashID)
  ) {
    return null; // Section headings don't have member type icons
  }

  // Look backwards to find the section this member belongs to
  for (let i = index - 1; i >= 0; i -= 1) {
    const prevHeading = allHeadings[i];
    const prevSlug = prevHeading.slug.toLowerCase();

    // Check if previous heading is a section heading
    if (prevSlug === propertiesID || prevSlug === `${propertiesID}-inherit-`) {
      return MemberType.Property;
    }
    if (prevSlug === methodsID || prevSlug === `${methodsID}-inherit-`) {
      return MemberType.Method;
    }
    if (prevSlug === eventsID || prevSlug === `${eventsID}-inherit-`) {
      return MemberType.Event;
    }
    if (prevSlug === callbacksID || prevSlug === `${callbacksID}-inherit-`) {
      return MemberType.Callback;
    }
    if (prevSlug === functionsID || prevSlug === `${functionsID}-inherit-`) {
      return MemberType.Function;
    }
    if (prevSlug === constructorsID || prevSlug === `${constructorsID}-inherit-`) {
      return MemberType.Constructor;
    }
    if (prevSlug === mathOperationsID || prevSlug === `${mathOperationsID}-inherit-`) {
      return MemberType.MathOperation;
    }

    // Also check for section hash patterns
    if (prevSlug in memberTypeBySectionHash) {
      return memberTypeBySectionHash[prevSlug as keyof typeof memberTypeBySectionHash];
    }
  }

  return null;
};
